var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { h, Fragment } from 'preact';
import { useState } from 'preact/hooks';
import { addLinesToShopifyCart, openCartDrawerFromAddResponse } from '../../utils/shopifyCart';
import { formatDisplayPrice } from '../../platforms/utils/priceUtils';
import { QuickViewModal } from './QuickViewModal';
import styles from './ProductCard.module.scss';
const LOW_STOCK_THRESHOLD = 5;
function isInStockFromSsAvailable(product) {
    var _a;
    const raw = (_a = product.customAttributes) === null || _a === void 0 ? void 0 : _a.ss_available;
    if (typeof raw === 'string') {
        return raw === '1' || raw === 'true';
    }
    return product.availability === 'in_stock';
}
/** Exactly one purchasable variant — safe for direct add-to-cart. */
function getSingleAvailableVariant(product) {
    const variants = product.variants;
    if (!(variants === null || variants === void 0 ? void 0 : variants.length))
        return null;
    const available = variants.filter(v => v.available);
    if (available.length !== 1)
        return null;
    const id = Number(available[0].id);
    return Number.isFinite(id) ? available[0] : null;
}
/** Sum of inventory across variants with numeric quantity; null if none reported. */
function getVariantsTotalInventory(product) {
    const variants = product.variants;
    if (!(variants === null || variants === void 0 ? void 0 : variants.length))
        return null;
    let total = 0;
    let hasAny = false;
    for (const v of variants) {
        if (typeof v.inventoryQuantity === 'number' && Number.isFinite(v.inventoryQuantity)) {
            hasAny = true;
            total += Math.max(0, v.inventoryQuantity);
        }
    }
    return hasAny ? total : null;
}
function getStockRowState(product, inStock) {
    if (!inStock)
        return { kind: 'sold_out' };
    const totalQty = getVariantsTotalInventory(product);
    if (totalQty != null && totalQty > 0 && totalQty < LOW_STOCK_THRESHOLD) {
        return { kind: 'low_stock', totalQty };
    }
    return { kind: 'available' };
}
function StockStatusRow({ state }) {
    if (state.kind === 'sold_out') {
        return (h("div", { class: `${styles.stockStatus} ${styles.stockStatusSoldOut}` },
            h("span", { class: styles.stockDotSoldOut, "aria-hidden": true }),
            "Sold Out"));
    }
    if (state.kind === 'low_stock') {
        return (h("div", { class: `${styles.stockStatus} ${styles.stockStatusLowStock}` },
            h("span", { class: styles.stockDotLowStock, "aria-hidden": true }),
            state.totalQty,
            " Last Item",
            state.totalQty > 1 ? 's' : ''));
    }
    return (h("div", { class: `${styles.stockStatus} ${styles.stockStatusAvailable}` },
        h("span", { class: styles.stockDotAvailable, "aria-hidden": true }),
        "Available"));
}
export function ProductCard({ product, layout = 'gridFour' }) {
    var _a, _b;
    const [quickViewOpen, setQuickViewOpen] = useState(false);
    const [cartAdding, setCartAdding] = useState(false);
    const { title, price, image, brand, promoMessage } = product;
    const singleAvailableVariant = getSingleAvailableVariant(product);
    const inStock = isInStockFromSsAvailable(product);
    const stockRowState = getStockRowState(product, inStock);
    const isImageObj = (img) => typeof img === 'object' && 'url' in img;
    const imgUrl = isImageObj(image) ? image.url : image;
    const imgAlt = isImageObj(image) ? image.alt : title;
    const tags = product.tags;
    const currentPrice = (_b = (_a = price.sale) !== null && _a !== void 0 ? _a : price.special) !== null && _b !== void 0 ? _b : price.regular;
    const hasDiscount = currentPrice < price.regular;
    const discountPct = hasDiscount
        ? Math.floor(((price.regular - currentPrice) / price.regular) * 100)
        : 0;
    if (layout === 'list') {
        return (h(Fragment, null,
            h("div", { class: styles.productCardList, onClick: () => setQuickViewOpen(true), style: { cursor: 'pointer' } },
                h("div", { class: styles.imageWrapperList },
                    h("img", { src: imgUrl, alt: imgAlt })),
                h("div", { class: styles.productInfo },
                    brand && h("p", { class: styles.brand }, brand.toUpperCase()),
                    h("p", { class: styles.productTitle }, title),
                    h("div", { class: styles.priceRow },
                        h("span", { class: styles.price },
                            "$",
                            formatDisplayPrice(currentPrice, tags)),
                        hasDiscount && h("span", { class: styles.rrp },
                            "RRP $",
                            formatDisplayPrice(price.regular, tags))),
                    h(StockStatusRow, { state: stockRowState }),
                    promoMessage && h("div", { class: styles.promoTag },
                        "\u2022 ",
                        promoMessage))),
            h(QuickViewModal, { product: product, isOpen: quickViewOpen, onClose: () => setQuickViewOpen(false) })));
    }
    return (h(Fragment, null,
        h("a", { class: styles.productCard, style: { cursor: 'pointer' }, href: `/products/${product.handle}` },
            h("div", { class: styles.imageWrapper },
                hasDiscount && (h("span", { class: styles.discountBadge },
                    "- ",
                    discountPct,
                    "%")),
                h("img", { src: imgUrl, alt: imgAlt }),
                h("button", { class: styles.mobileQuickViewToggle, type: "button", onClick: e => {
                        e.preventDefault();
                        e.stopPropagation();
                        setQuickViewOpen(true);
                    } },
                    h("svg", { xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", viewBox: "0 0 16 16", fill: "none" },
                        h("mask", { id: "mask0_3927_24750", style: "mask-type:alpha", maskUnits: "userSpaceOnUse", x: "0", y: "0", width: "16", height: "16" },
                            h("rect", { width: "16", height: "16", fill: "#D9D9D9" })),
                        h("g", { mask: "url(#mask0_3927_24750)" },
                            h("path", { d: "M8.00196 10.3847C8.7574 10.3847 9.39902 10.1203 9.92679 9.5915C10.4546 9.06272 10.7185 8.42061 10.7185 7.66517C10.7185 6.90972 10.4541 6.26811 9.92529 5.74033C9.39651 5.21256 8.7544 4.94867 7.99896 4.94867C7.24351 4.94867 6.6019 5.21306 6.07413 5.74183C5.54635 6.27061 5.28246 6.91272 5.28246 7.66817C5.28246 8.42361 5.54685 9.06522 6.07563 9.593C6.6044 10.1208 7.24651 10.3847 8.00196 10.3847ZM8.00046 9.46667C7.50046 9.46667 7.07546 9.29167 6.72546 8.94167C6.37546 8.59167 6.20046 8.16667 6.20046 7.66667C6.20046 7.16667 6.37546 6.74167 6.72546 6.39167C7.07546 6.04167 7.50046 5.86667 8.00046 5.86667C8.50046 5.86667 8.92546 6.04167 9.27546 6.39167C9.62546 6.74167 9.80046 7.16667 9.80046 7.66667C9.80046 8.16667 9.62546 8.59167 9.27546 8.94167C8.92546 9.29167 8.50046 9.46667 8.00046 9.46667ZM8.00046 12.3333C6.59701 12.3333 5.31346 11.9589 4.14979 11.2102C2.98613 10.4615 2.05729 9.4765 1.36329 8.25517C1.30774 8.15939 1.26712 8.06289 1.24146 7.96567C1.2159 7.86844 1.20312 7.76867 1.20312 7.66633C1.20312 7.564 1.2159 7.46433 1.24146 7.36733C1.26712 7.27033 1.30774 7.17394 1.36329 7.07817C2.05729 5.85683 2.98613 4.87183 4.14979 4.12317C5.31346 3.37439 6.59701 3 8.00046 3C9.4039 3 10.6875 3.37439 11.8511 4.12317C13.0148 4.87183 13.9436 5.85683 14.6376 7.07817C14.6932 7.17394 14.7338 7.27044 14.7595 7.36767C14.785 7.46489 14.7978 7.56467 14.7978 7.667C14.7978 7.76933 14.785 7.869 14.7595 7.966C14.7338 8.063 14.6932 8.15939 14.6376 8.25517C13.9436 9.4765 13.0148 10.4615 11.8511 11.2102C10.6875 11.9589 9.4039 12.3333 8.00046 12.3333ZM8.00046 11.3333C9.25601 11.3333 10.4088 11.0028 11.4588 10.3417C12.5088 9.68055 13.3116 8.78889 13.8671 7.66667C13.3116 6.54444 12.5088 5.65278 11.4588 4.99167C10.4088 4.33056 9.25601 4 8.00046 4C6.7449 4 5.59213 4.33056 4.54213 4.99167C3.49213 5.65278 2.68935 6.54444 2.13379 7.66667C2.68935 8.78889 3.49213 9.68055 4.54213 10.3417C5.59213 11.0028 6.7449 11.3333 8.00046 11.3333Z", fill: "black" })))),
                h("div", { class: styles.addToCartWrapper },
                    singleAvailableVariant && (h("button", { type: "button", class: styles.addToCart, disabled: cartAdding, onClick: (e) => __awaiter(this, void 0, void 0, function* () {
                            e.preventDefault();
                            e.stopPropagation();
                            const id = Number(singleAvailableVariant.id);
                            if (!Number.isFinite(id))
                                return;
                            setCartAdding(true);
                            try {
                                const res = yield addLinesToShopifyCart([{ id, quantity: 1 }]);
                                if (!res.ok)
                                    throw new Error('Cart error');
                                yield openCartDrawerFromAddResponse(res);
                            }
                            catch (_a) {
                                console.error('Add to cart failed');
                            }
                            finally {
                                setCartAdding(false);
                            }
                        }) }, cartAdding ? 'Adding…' : 'Add To Cart')),
                    h("button", { type: "button", class: styles.addToCartSecondary, onClick: e => {
                            e.preventDefault();
                            e.stopPropagation();
                            setQuickViewOpen(true);
                        } }, "Quick View"))),
            h("div", { class: styles.productInfo },
                brand && h("p", { class: styles.brand }, brand.toUpperCase()),
                h("p", { class: styles.productTitle }, title),
                h("div", { class: styles.priceRow },
                    h("span", { class: styles.price },
                        "$",
                        formatDisplayPrice(currentPrice, tags)),
                    hasDiscount && h("span", { class: styles.rrp },
                        "RRP $",
                        formatDisplayPrice(price.regular, tags))),
                h(StockStatusRow, { state: stockRowState }),
                promoMessage && h("div", { class: styles.promoTag },
                    "\u2022 ",
                    promoMessage))),
        h(QuickViewModal, { product: product, isOpen: quickViewOpen, onClose: () => setQuickViewOpen(false) })));
}
